/**
 * Typed adapter rejections — ADR-008 §7. Recoverable liberties go in the
 * ImportReport; these are the UNRECOVERABLE ones: no silent skip, no default.
 * Depends on: nothing. Depended on by: convert-rig, convert-clip, load-glb.
 */

export class AdapterError extends Error {
  constructor(message: string) {
    super(message);
    this.name = new.target.name;
  }
}

/** The file loaded but contains no skinned skeleton (ADR-008 §7). */
/**
 * Two skins of one connected skeleton disagree about a shared bone's inverse
 * bind (Codex P2, #108): the rig's world bind would then depend on which skin
 * happened to be walked first — helper count, traversal order — so the
 * component is refused, naming the bone and the skins.
 */
export class InconsistentBindError extends AdapterError {
  constructor(
    readonly label: string,
    readonly bone: string,
    readonly skins: readonly [string, string],
    /** Which component disagreed and by how much, in its own units (metres, degrees, ratio). */
    readonly disagreement: string,
  ) {
    super(
      `"${label}": skins "${skins[0]}" and "${skins[1]}" share bone "${bone}" but bind it differently ` +
        `(${disagreement}) — one skeleton cannot have two binds for one bone.`,
    );
    this.name = 'InconsistentBindError';
  }
}

export class NoSkeletonError extends AdapterError {
  constructor(readonly label: string) {
    super(`"${label}" contains no skinned skeleton — nothing to import as a rig.`);
  }
}

/** More than one distinct skeleton; the caller must select explicitly — no silent first-pick. */
export class MultipleSkeletonsError extends AdapterError {
  constructor(
    readonly label: string,
    readonly candidates: readonly string[],
  ) {
    super(
      `"${label}" contains ${candidates.length} distinct skeletons: ` +
        `${candidates.join(', ')}. Pass selectSkeleton to choose one explicitly (ADR-008 §7).`,
    );
  }
}

/** A bind matrix with shear (or zero scale) that TRS decomposition cannot represent faithfully. */
export class NonDecomposableBindError extends AdapterError {
  constructor(
    readonly boneName: string,
    readonly maxElementError: number,
  ) {
    super(
      `Bind matrix of bone "${boneName}" does not survive TRS decomposition ` +
        `(recomposition error ${maxElementError.toExponential(2)}) — shear or degenerate scale. ` +
        `The kernel IR cannot represent this bind faithfully (ADR-008 §7).`,
    );
  }
}

/**
 * Improper (negative-determinant) bind frames that the reflection scan could
 * not resolve into declared mirror pairs. Representable reflections become
 * `reflectedPairs` and are rejected by the kernel's guard with an assisted
 * path; UNREPRESENTABLE ones must not enter the IR looking clean — the
 * silent-mangle this guards against is v2's costliest reflection bug.
 */
export class ImproperBindFrameError extends AdapterError {
  constructor(
    readonly label: string,
    readonly boneNames: readonly string[],
  ) {
    super(
      `"${label}" has ${boneNames.length} improper (reflected) bind frame(s) not resolvable ` +
        `into declared mirror pairs: ${boneNames.join(', ')}. ` +
        `Refusing to import a reflection the IR cannot declare (ADR-005 §7, ADR-008 §7).`,
    );
  }
}

/**
 * A Z-up FBX: FBXLoader normalizes the declared up-axis by rotating the
 * RETURNED ROOT — a correction that lives outside the inverse-bind space
 * conversion extracts from (ADR-008 §2b: world bind comes from IBMs only),
 * so the converted rig would stay Z-up while a 'Y' declaration lied about
 * it. Declaring 'Z' instead needs a facing answer this loader does not
 * surface (and the kernel rejects degenerate Z-up/±Z-facing pairs). No
 * specimen exercises this path yet — refuse loudly until one does.
 */
export class ZUpFbxError extends AdapterError {
  constructor() {
    super(
      `This FBX declares a Z-up coordinate system (FBXLoader rotated the scene root to ` +
        `compensate). The root correction is outside the IBM bind space, so conversion ` +
        `cannot yet declare this file's conventions honestly — refusing to guess ` +
        `(ADR-008 §2c). Support lands with a Z-up specimen.`,
    );
  }
}

/**
 * The FBX GlobalSettings declare an axis system this stage cannot map
 * honestly: FBXLoader compensates UpAxis only and ignores Front/Coord, so
 * any declared tuple beyond Y-up/+Z-front/+X-coord (Maya Y-up, the
 * specimen-proven system) would enter the RigDescriptor as a known-false
 * convention declaration — corrupted root motion, not a loud failure.
 */
export class UnsupportedFbxAxesError extends AdapterError {
  constructor(readonly violations: readonly string[]) {
    super(
      `This FBX declares an axis system this stage cannot map honestly: ` +
        `${violations.join(', ')}. Refusing to import under a false convention ` +
        `declaration (ADR-008 §2c). Support requires a specimen of this system.`,
    );
  }
}

/**
 * The FBX byte scan and FBXLoader's own GlobalSettings parse disagree on
 * UnitScaleFactor. Both read the same file; disagreement means the scan
 * matched the wrong record (section-order assumption broken) — and a wrong
 * unit declaration is a silent 100×-scale import (the PR #17 bug class).
 */
export class FbxUnitDisagreementError extends AdapterError {
  constructor(
    readonly scanned: number,
    readonly fromLoader: number,
  ) {
    super(
      `FBX UnitScaleFactor disagreement: byte scan read ${scanned}, FBXLoader read ${fromLoader}. ` +
        `Refusing to declare units from conflicting evidence (ADR-008 §2c).`,
    );
  }
}

/**
 * A BVH convention override that resolves to a facing PARALLEL to the up-axis.
 * BVH declares neither unit nor up-axis, so a Z-up corpus (SL/OpenSim) overrides
 * upAxis to 'Z' — but the facing default ('+Z') then names no sagittal plane:
 * the kernel rejects `upAxis Z` with `±Z` facing (rig-descriptor.ts), and facing
 * is only ever ±X/±Z, so a Z-up corpus MUST declare a facing perpendicular to Z
 * (+X or -X). Refusing here — at the format boundary, with the fix named — beats
 * emitting a convention that blows up cryptically in downstream rig validation.
 */
export class DegenerateBvhConventionError extends AdapterError {
  constructor(
    readonly upAxis: string,
    readonly facing: string,
  ) {
    super(
      `BVH convention is degenerate: upAxis ${upAxis} with facing ${facing} — the facing is ` +
        `parallel to up and names no sagittal plane. BVH declares no facing, so a ${upAxis}-up ` +
        `corpus must declare one perpendicular to ${upAxis} (+X or -X). Refusing to import under ` +
        `a convention the kernel rejects (ADR-008 §2c).`,
    );
  }
}

/**
 * A non-bone ancestor (e.g. an Armature) with a non-identity ROTATION sits
 * above a bone carrying a translation track. Rescaling the track by the
 * interposed transform is exact, but folding a rotating interposed frame into
 * animation tracks is a path no specimen has exercised — we refuse to emit a
 * guessed transform rather than ship subtly-wrong motion (fail-loud, ADR-008 §2b).
 */
export class InterposedFrameRotationError extends AdapterError {
  constructor(readonly boneName: string) {
    super(
      `Bone "${boneName}" has a non-bone ancestor with a non-identity rotation and an ` +
        `animated translation track. Folding a rotating interposed frame into animation ` +
        `tracks is not yet validated — refusing to guess (ADR-008 §2b).`,
    );
  }
}

/**
 * SL .anim version gate: only version 1.0 (the Bento-era format every surveyed
 * file uses — 4,940/4,940 across prod + local corpora, 2026-08-08 census) is
 * decodable. v0.x never shipped publicly; anything else is not this format.
 */
export class UnsupportedSlAnimVersionError extends AdapterError {
  constructor(
    readonly label: string,
    readonly version: number,
    readonly subVersion: number,
  ) {
    super(
      `"${label}": SL .anim version ${version}.${subVersion} is not the supported 1.0 — ` +
        `either not an LLKeyframeMotion file or a pre-release format. Refusing to guess.`,
    );
  }
}

/**
 * Structural refusal for SL .anim bytes: a key count that overruns the buffer
 * (the census found source assets truncated MID-FILE — 2 unique UUIDs, a joint
 * block 7 bytes short), a missing string terminator, or trailing bytes after
 * the constraint block (exact-EOF is a format assertion: 2,229/2,233 local
 * files end exactly where the layout says). The row keeps its source bytes and
 * is marked failed — never silently skipped, never partially decoded
 * (ADR-191 §5 fail-loud).
 */
export class MalformedSlAnimError extends AdapterError {
  constructor(
    readonly label: string,
    readonly detail: string,
    readonly offset: number,
  ) {
    super(`"${label}": malformed SL .anim at byte ${offset}: ${detail}`);
  }
}
